import { useState, useRef, useEffect } from 'react';

interface CustomEffectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, description: string) => void;
}

export function CustomEffectModal({ isOpen, onClose, onAdd }: CustomEffectModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onAdd(trimmed, description.trim());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background-primary/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-sm bg-background-card border-2 border-accent-bronze/50
          rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="px-4 py-3 bg-background-elevated border-b border-background-primary">
          <h2 className="font-display text-lg text-text-primary tracking-wide">Add Effect</h2>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">
              Name
            </label>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="e.g., Bless, Haste, Shield of Faith"
              className="w-full px-3 py-2 text-sm bg-background-primary border-2 border-background-elevated
                rounded-lg text-text-primary placeholder:text-text-muted
                focus:outline-none focus:border-accent-bronze transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mechanical effects, duration, etc."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-background-primary border-2 border-background-elevated
                rounded-lg text-text-primary placeholder:text-text-muted resize-none
                focus:outline-none focus:border-accent-bronze transition-colors"
            />
          </div>
        </div>

        <div className="flex gap-2 p-4 pt-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 font-display text-sm uppercase tracking-wide
              bg-background-elevated hover:bg-background-primary text-text-secondary
              hover:text-text-primary rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="flex-1 px-4 py-2 font-display text-sm uppercase tracking-wide
              bg-accent-gold hover:bg-accent-gold-light text-background-primary
              rounded transition-colors shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
